import React, { useState, useMemo } from 'react';
import { Box, Typography, IconButton, Popover, Divider } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr || !timeStr.includes(':')) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return (hours * 60) + minutes;
};

const formatMinutesToHoursAndMins = (minutes) => {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(Math.round(minutes % 60)).padStart(2, '0')}`;
};

const calculateMetrics = (startYear, startMonth, endYear, endMonth, userCount, statEvents, holidayList) => {
  if (!startYear || !endYear || !startMonth || !endMonth) return { holidays: [], summary: { totalHolidayDays: 0 }, monthly: [] };

  const startStr = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;
  const endStr = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(new Date(endYear, endMonth, 0).getDate()).padStart(2, '0')}`;
  const todayStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;

  const holidays = holidayList.filter(h => h.holidayDate >= startStr && h.holidayDate <= endStr).sort((a, b) => a.holidayDate.localeCompare(b.holidayDate));

  let totalPeriodHolidayCount = 0;
  holidays.forEach(h => {
    totalPeriodHolidayCount += (h.workType === 'FULL' ? 1.0 : 0.5);
  });

  const vacationData = statEvents.find(item => item.actionCode === 'VACTION' || item.actionName === '휴가');
  const totalVacationMins = vacationData && vacationData.totalTime ? parseTimeToMinutes(vacationData.totalTime) : 0;
  const totalVacationDaysVal = Number((totalVacationMins / 480).toFixed(2));

  const map = {};
  let cur = new Date(Number(startYear), startMonth - 1, 1);
  const end = new Date(Number(endYear), endMonth, 0);

  while (cur <= end) {
    if (cur.getDay() !== 0 && cur.getDay() !== 6) {
      const key = `${cur.getFullYear()}년 ${cur.getMonth() + 1}월`;
      const dateStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
      
      if (!map[key]) {
        map[key] = { total: 0, passed: 0, targetHoliday: 0, passedHoliday: 0 };
      }
      
      map[key].total += 1;
      
      const holidaySetting = holidays.find(h => h.holidayDate === dateStr);
      let holidayValue = 0;
      if (holidaySetting) {
        holidayValue = holidaySetting.workType === 'FULL' ? 1.0 : 0.5;
      }
      
      map[key].targetHoliday += holidayValue;

      if (dateStr <= todayStr) {
        map[key].passed += 1;
        map[key].passedHoliday += holidayValue;
      }
    }
    cur.setDate(cur.getDate() + 1);
  }

  const monthly = Object.keys(map).map(key => {
    const item = map[key];
    
    let currentMonthVacationDays = 0;
    if (key.includes(`${new Date().getFullYear()}년 ${new Date().getMonth() + 1}월`)) {
      currentMonthVacationDays = totalVacationDaysVal;
    } else if (Object.keys(map).length === 1) {
      currentMonthVacationDays = totalVacationDaysVal;
    }

    const targetHolidayDays = item.targetHoliday * userCount;
    const finalTargetDays = Number((item.total * userCount - targetHolidayDays - currentMonthVacationDays).toFixed(2));

    const passedHolidayDays = item.passedHoliday * userCount;
    const finalPassedDays = Number((item.passed * userCount - passedHolidayDays - currentMonthVacationDays).toFixed(2));

    return {
      monthLabel: key,
      weekdayCount: item.total,
      passedWeekdayCount: item.passed,
      targetHolidayDays,
      finalTargetDays,
      passedHolidayDays,
      totalVacationDaysVal: currentMonthVacationDays,
      finalPassedDays
    };
  });

  return { holidays, summary: { totalHolidayDays: Number((totalPeriodHolidayCount * userCount).toFixed(1)) }, monthly, totalVacationMins, totalVacationDaysVal };
};

function SalesActionStatWorkingBaseCard({ 
  statEvents = [], 
  holidayList = [], 
  targetWorkMinutes, 
  baseWorkMinutes, 
  startYear, 
  startMonth, 
  endYear, 
  endMonth, 
  userCount = 1 
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const { holidays, summary, monthly, totalVacationMins, totalVacationDaysVal } = useMemo(
    () => calculateMetrics(startYear, startMonth, endYear, endMonth, userCount, statEvents, holidayList),
    [startYear, startMonth, endYear, endMonth, userCount, statEvents, holidayList]
  );

  const totalRangeDays = useMemo(() => {
    if (!targetWorkMinutes) return 0;
    const rawRangeDays = Number(((targetWorkMinutes * userCount) / 480).toFixed(1));
    return Number((rawRangeDays - totalVacationDaysVal).toFixed(2));
  }, [targetWorkMinutes, userCount, totalVacationDaysVal]);

  const dynamicWorkingDays = useMemo(() => {
    if (!baseWorkMinutes) return 0;
    const rawWorkingDays = Number(((baseWorkMinutes * userCount) / 480).toFixed(1));
    return Number((rawWorkingDays - totalVacationDaysVal).toFixed(2));
  }, [baseWorkMinutes, userCount, totalVacationDaysVal]);

  const open = Boolean(anchorEl);
  return (
    <Box sx={{ p: 3, backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)', border: '1px solid rgba(0, 0, 0, 0.03)', height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: '600', color: '#111827' }}>근무 기준 ({userCount}명)</Typography>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: 'text.secondary' }}><HelpOutlineIcon fontSize="small" /></IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {[
          { label: '총 기준 일수', days: totalRangeDays, mins: totalRangeDays * 480, color: '#1F2937' },
          { label: '총 누적 일수', days: dynamicWorkingDays, mins: dynamicWorkingDays * 480, color: '#4F46E5' },
          { label: '휴일', days: summary.totalHolidayDays, mins: summary.totalHolidayDays * 480, color: '#1F2937' },
          { label: '휴가 합계', days: totalVacationDaysVal, mins: totalVacationMins, color: '#1F2937' }
        ].map((item, i, arr) => (
          <Box 
            key={i} 
            sx={{ 
              borderBottom: i === arr.length - 1 ? 'none' : '1px solid #f3f4f6', 
              pb: i === arr.length - 1 ? 0 : 0.75 
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: '500' }}>{item.label}</Typography>
              <Typography variant="body2" sx={{ fontWeight: '600', color: item.color }}>{item.days}일</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: '500', fontSize: '11px' }}>({formatMinutesToHoursAndMins(item.mins)})</Typography>
            </Box>
          </Box>
        ))}
      </Box>

            <Popover 
              open={open} 
              anchorEl={anchorEl} 
              onClose={() => setAnchorEl(null)} 
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} 
              transformOrigin={{ vertical: 'top', horizontal: 'right' }} 
              PaperProps={{ sx: { p: 2.5, maxWidth: 540, maxHeight: 450, overflowY: 'auto', borderRadius: '8px', boxShadow: '0px 4px 16px rgba(0,0,0,0.12)' } }} 
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {monthly.map((m, idx) => (
                  <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: '700', color: '#111827' }}>{m.monthLabel} 일수 산정 방식</Typography>
                    <Box sx={{ pl: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: '600', color: '#374151', display: 'block', mb: 0.5 }}>• 총 기준 일수 공식</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5, mb: 1.5, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {m.weekdayCount}일(근무일수) × {userCount}명 - {m.targetHolidayDays / userCount}일(휴일) × {userCount}명 - {m.totalVacationDaysVal}일(휴가) = <b>{m.finalTargetDays}일</b> ({formatMinutesToHoursAndMins(m.finalTargetDays * 480)})
                      </Typography>

                      <Typography variant="caption" sx={{ fontWeight: '600', color: '#4F46E5', display: 'block', mb: 0.5 }}>• 총 누적 일수 공식</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5, mb: 1.5, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {m.passedWeekdayCount}일(근무일수) × {userCount}명 - {m.passedHolidayDays / userCount}일(휴일) × {userCount}명 - {m.totalVacationDaysVal}일(휴가) = <b>{m.finalPassedDays}일</b> ({formatMinutesToHoursAndMins(m.finalPassedDays * 480)})
                      </Typography>

                      <Typography variant="caption" sx={{ fontWeight: '600', color: '#374151', display: 'block', mb: 0.5 }}>• 휴일 일수 산정</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5, mb: 1.5, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        선택 기간 내 지정된 총 휴일 수({m.targetHolidayDays / userCount}일) × {userCount}명 = <b>{m.targetHolidayDays}일</b> ({formatMinutesToHoursAndMins(m.targetHolidayDays * 480)})
                      </Typography>

                      <Typography variant="caption" sx={{ fontWeight: '600', color: '#374151', display: 'block', mb: 0.5 }}>• 휴가 일수 산정</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        부서원들의 실제 등록 휴가 시간 합계({m.totalVacationMins ? formatMinutesToHoursAndMins(m.totalVacationMins) : '00:00'}) ÷ 하루 8시간(480분) = <b>{m.totalVacationDaysVal}일</b>
                      </Typography>
                    </Box>
                    {idx < monthly.length - 1 && <Divider sx={{ my: 1 }} />}
                  </Box>
                ))}
                <Divider sx={{ my: 0.5 }} />
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: '600', color: '#111827', display: 'block', mb: 0.75 }}>선택 기간 내 상세 휴일 내역 ({holidays.length}건)</Typography>
                  {holidays.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 0.5 }}>
                      {holidays.map((h, idx) => (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', whiteSpace: 'nowrap' }}>
                          <Typography variant="caption" color="text.secondary">{idx + 1}. {h.holidayDate}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ pl: 2, flexGrow: 1, textOverflow: 'ellipsis', overflow: 'hidden' }}>{h.comment}</Typography>
                          <Typography variant="caption" sx={{ fontWeight: '500', color: h.workType === 'FULL' ? '#EF4444' : '#F59E0B', pl: 4 }}>{h.workType === 'FULL' ? '종일 휴일 (1.0일)' : '반일 휴가 (0.5일)'}</Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : <Typography variant="caption" color="text.disabled" sx={{ pl: 0.5 }}>해당 기간 내에 지정된 휴일이 없습니다.</Typography>}
                </Box>
              </Box>
            </Popover>

    </Box>
  );
}

export default SalesActionStatWorkingBaseCard;
